import { fetchTemplateBodies, fetchTemplates } from "./api";
import { hashSourceText } from "./contentHash";
import { parseLexicalBody, matchTranslations } from "./lexicalParser";
import { StoredSegmentState, syncSegmentsWithStored } from "./segmentSync";
import { fetchAllDocumentStates, upsertDocumentState } from "./translationStateApi";
export interface TranslatedTemplatePair {
  sheet: string;
  enTemplateId: number;
  frTemplateId: number | null;
  enTitle?: string;
  frTitle?: string;
}
export type ReimportMode = "incomplete" | "templateIds";
export interface BulkApproveItemResult {
  documentPairKey: string;
  enTemplateId: number;
  frTemplateId: number | null;
  title: string;
  sheet: string;
  totalSegments: number;
  approvedSegments: number;
  skippedMissingFr: number;
  success: boolean;
  error?: string;
}
export interface BulkApproveReport {
  total: number;
  succeeded: number;
  failed: number;
  approvedSegments: number;
  items: BulkApproveItemResult[];
}
export function documentPairKeyForTemplate(enTemplateId: number): string {
  return `pair-${enTemplateId}`;
}

/** Parse pasted IDs like "241, 240" or "#278 #383" into unique EN template IDs. */
export function parseTemplateIdInput(raw: string): number[] {
  const matches = raw.match(/\d+/g) || [];
  const ids: number[] = [];
  const seen = new Set<number>();
  for (const match of matches) {
    const id = Number(match);
    if (!Number.isFinite(id) || id <= 0 || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}
export function isDocumentStateIncomplete(states: StoredSegmentState[] | undefined): boolean {
  if (!states?.length) return true;
  const active = states.filter(s => s.status !== "removed");
  if (!active.length) return true;
  const approved = active.filter(s => s.status === "approved").length;
  return approved < active.length;
}

/**
 * Build reimport targets from Document Hub + server progress.
 * No fixed catalog — any EN template with a FR pair is eligible.
 */
export async function resolveReimportPairs(options: {
  mode: ReimportMode;
  templateIds?: number[];
}): Promise<TranslatedTemplatePair[]> {
  const templates = await fetchTemplates();
  const enById = new Map(templates.filter(t => t.locale === "en").map(t => [t.id, t]));
  const frById = new Map(templates.filter(t => t.locale === "fr").map(t => [t.id, t]));
  const toPair = (enTemplateId: number, frTemplateId?: number | null): TranslatedTemplatePair | null => {
    const en = enById.get(enTemplateId);
    if (!en) return null;
    const resolvedFrId = frTemplateId || en.localeTemplateIds?.fr || null;
    if (!resolvedFrId) return null;
    const fr = frById.get(resolvedFrId);
    return {
      sheet: "document-hub",
      enTemplateId,
      frTemplateId: resolvedFrId,
      enTitle: en.title,
      frTitle: fr?.title
    };
  };
  if (options.mode === "templateIds") {
    const pairs: TranslatedTemplatePair[] = [];
    for (const id of options.templateIds || []) {
      const pair = toPair(id);
      if (pair) pairs.push(pair);
    }
    return pairs;
  }
  const records = await fetchAllDocumentStates().catch(() => []);
  const pairs: TranslatedTemplatePair[] = [];
  const seen = new Set<number>();
  for (const record of records) {
    if (!isDocumentStateIncomplete(record.segments)) continue;
    if (!record.enTemplateId || seen.has(record.enTemplateId)) continue;
    const pair = toPair(record.enTemplateId, record.frTemplateId);
    if (!pair) continue;
    seen.add(record.enTemplateId);
    pairs.push(pair);
  }
  return pairs;
}
export function buildApprovedStatesFromFrBodies(enBody: string, frBody: string | null): {
  states: StoredSegmentState[];
  approvedCount: number;
  totalCount: number;
  skippedMissingFr: number;
} {
  const parsedEn = parseLexicalBody(enBody);
  const enSegments = parsedEn.filter(s => s.isTranslatable);
  const frMatches = matchTranslations(parsedEn, frBody);
  const {
    states
  } = syncSegmentsWithStored(enSegments, [], frMatches);
  let approvedCount = 0;
  let skippedMissingFr = 0;
  const approvedStates = states.filter(s => s.status !== "removed").map((state, index) => {
    const frText = frMatches[index]?.trim();
    if (!frText) {
      skippedMissingFr++;
      return {
        ...state,
        status: "not_started" as const,
        mergedText: undefined,
        locked: false
      };
    }
    approvedCount++;
    return {
      ...state,
      status: "approved" as const,
      mergedText: frText,
      locked: true,
      sourceHash: hashSourceText(state.sourceText)
    };
  });
  return {
    states: approvedStates,
    approvedCount,
    totalCount: approvedStates.length,
    skippedMissingFr
  };
}
async function resolveFrTemplateId(enTemplateId: number, explicitFrTemplateId: number | null, templateFrByEnId: Map<number, number>): Promise<number | null> {
  if (explicitFrTemplateId) return explicitFrTemplateId;
  return templateFrByEnId.get(enTemplateId) ?? null;
}
export async function bulkApproveTranslatedTemplatePairs(pairs: TranslatedTemplatePair[], options?: {
  onProgress?: (info: {
    current: number;
    total: number;
    title: string;
  }) => void;
}): Promise<BulkApproveReport> {
  const templates = await fetchTemplates();
  const templateFrByEnId = new Map<number, number>();
  for (const template of templates) {
    const frId = template.localeTemplateIds?.fr;
    if (template.locale === "en" && frId) {
      templateFrByEnId.set(template.id, frId);
    }
  }
  const items: BulkApproveItemResult[] = [];
  let succeeded = 0;
  let failed = 0;
  let approvedSegments = 0;
  for (let index = 0; index < pairs.length; index++) {
    const pair = pairs[index];
    const title = pair.enTitle || `Template #${pair.enTemplateId}`;
    options?.onProgress?.({
      current: index + 1,
      total: pairs.length,
      title
    });
    const documentPairKey = documentPairKeyForTemplate(pair.enTemplateId);
    const frTemplateId = await resolveFrTemplateId(pair.enTemplateId, pair.frTemplateId, templateFrByEnId);
    if (!frTemplateId) {
      failed++;
      items.push({
        documentPairKey,
        enTemplateId: pair.enTemplateId,
        frTemplateId: null,
        title,
        sheet: pair.sheet,
        totalSegments: 0,
        approvedSegments: 0,
        skippedMissingFr: 0,
        success: false,
        error: "No paired French template found"
      });
      continue;
    }
    try {
      const {
        enBody,
        frBody
      } = await fetchTemplateBodies(pair.enTemplateId, frTemplateId);
      if (!enBody?.trim() || !frBody?.trim()) {
        throw new Error("Missing English or French template body");
      }
      const built = buildApprovedStatesFromFrBodies(enBody, frBody);
      if (built.approvedCount === 0) {
        throw new Error("No French segment text found to approve");
      }
      await upsertDocumentState({
        documentPairKey,
        enTemplateId: pair.enTemplateId,
        frTemplateId,
        segments: built.states,
        draftCache: {}
      });
      succeeded++;
      approvedSegments += built.approvedCount;
      items.push({
        documentPairKey,
        enTemplateId: pair.enTemplateId,
        frTemplateId,
        title,
        sheet: pair.sheet,
        totalSegments: built.totalCount,
        approvedSegments: built.approvedCount,
        skippedMissingFr: built.skippedMissingFr,
        success: true
      });
    } catch (error) {
      failed++;
      items.push({
        documentPairKey,
        enTemplateId: pair.enTemplateId,
        frTemplateId,
        title,
        sheet: pair.sheet,
        totalSegments: 0,
        approvedSegments: 0,
        skippedMissingFr: 0,
        success: false,
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }
  return {
    total: pairs.length,
    succeeded,
    failed,
    approvedSegments,
    items
  };
}